import React from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Users } from 'lucide-react';

/**
 * Chooses which public page a program is listed on.
 * Open Sessions are drop-in sessions with member / non-member pricing and are
 * flagged on the program document with isPayPal === true.
 */
export const ProgramListingSettings = ({ value, onPatch, idPrefix = 'listing' }) => {
  const isOpenSession = value.isPayPal === true;

  const handleChange = (e) => {
    if (e.target.value === 'open_session') {
      onPatch({
        isPayPal: true,
        scheduling_mode: 'term',
        memberPrice: value.memberPrice ?? 10,
        nonMemberPrice: value.nonMemberPrice ?? 25,
      });
    } else {
      onPatch({ isPayPal: false });
    }
  };

  return (
    <div className="border border-stone-200 rounded-md p-4 bg-stone-50" data-testid="program-listing-settings">
      <Label htmlFor={`${idPrefix}-listing-type`} className="font-medium text-stone-900 block mb-2">
        Program Type
      </Label>
      <select
        id={`${idPrefix}-listing-type`}
        value={isOpenSession ? 'open_session' : 'class'}
        onChange={handleChange}
        className="w-full px-3 py-2 border border-stone-300 rounded-md"
        data-testid="listing-type-select"
      >
        <option value="class">Class — listed on the Adult Classes / Kids Classes page</option>
        <option value="open_session">Open Session — listed on the Open Sessions page</option>
      </select>

      {isOpenSession && (
        <div className="mt-4 space-y-3" data-testid="open-session-fields">
          <p className="text-xs text-blue-900 bg-blue-50 border border-blue-200 rounded p-2.5 flex gap-2">
            <Users size={15} className="flex-shrink-0 mt-0.5" />
            <span>
              This appears on the <strong>Open Sessions</strong> page (not the Classes pages) with drop-in
              member / non-member pricing and instant PayPal registration.
            </span>
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor={`${idPrefix}-session-type-label`}>Session Type Label</Label>
              <Input
                id={`${idPrefix}-session-type-label`}
                value={value.session_type_label ?? ''}
                onChange={(e) => onPatch({ session_type_label: e.target.value })}
                placeholder="e.g. Figure, Costumed Model, Long Pose"
                className="mt-1"
                data-testid="open-session-type-label"
              />
              <p className="text-xs text-stone-500 mt-1">Shown as the first pill on the card. Leave blank to use the category.</p>
            </div>
            <div>
              <Label htmlFor={`${idPrefix}-session-format-note`}>Format / Pose Details</Label>
              <Input
                id={`${idPrefix}-session-format-note`}
                value={value.session_format_note ?? ''}
                onChange={(e) => onPatch({ session_format_note: e.target.value })}
                placeholder="e.g. Live model, 3 long poses with breaks"
                className="mt-1"
                data-testid="open-session-format-note"
              />
              <p className="text-xs text-stone-500 mt-1">Shown as the "Format:" line. Leave blank to hide it.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Member Price ($)</Label>
              <Input
                type="number"
                min="0"
                value={value.memberPrice ?? 0}
                onChange={(e) => onPatch({ memberPrice: parseFloat(e.target.value) || 0 })}
                className="mt-1"
                data-testid="open-session-member-price"
              />
            </div>
            <div>
              <Label>Non-Member Price ($)</Label>
              <Input
                type="number"
                min="0"
                value={value.nonMemberPrice ?? 0}
                onChange={(e) => onPatch({ nonMemberPrice: parseFloat(e.target.value) || 0 })}
                className="mt-1"
                data-testid="open-session-nonmember-price"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
